import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { Spot, GarageCar, GarageMod, TrackSession, FavoriteList, User, Comment, MaintenanceRecord, MaintenanceScheduleItem } from '../types';
import { useAuth } from './AuthContext';
import { spotsApi, garageApi, favoritesApi, type SpotApi, type SpotFeedStatsApi, type GarageCarApi, type GarageModApi, type MaintenanceRecordApi, type MaintenanceScheduleItemApi } from '../api/client';

const INITIAL_SPOT_PAGE_SIZE = 10;

function mergeSpotsById(existing: Spot[], incoming: Spot[]): Spot[] {
  const byId = new Map(existing.map((spot) => [spot.id, spot]));
  incoming.forEach((spot) => {
    byId.set(spot.id, spot);
  });
  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

export function spotApiToSpot(s: SpotApi): Spot {
  return {
    id: s.id,
    authorId: s.authorId,
    author: s.author ? { ...s.author, topBadges: s.author.topBadges } : undefined,
    imageUrl: s.imageUrl,
    images: Array.isArray(s.images) ? s.images : (s.imageUrl ? [s.imageUrl] : []),
    caption: s.caption,
    car: s.car as unknown as Spot['car'],
    location: s.location,
    city: s.city,
    countryCode: s.countryCode,
    event: s.event,
    taggedOwnerId: s.taggedOwnerId,
    taggedOwner: s.taggedOwner,
    likes: s.likes,
    beautiful: Array.isArray(s.beautiful) ? s.beautiful : [],
    cool: Array.isArray(s.cool) ? s.cool : [],
    sweetPhoto: Array.isArray(s.sweetPhoto) ? s.sweetPhoto : [],
    comments: s.comments as Comment[],
    likeCount: s.likeCount,
    beautifulCount: s.beautifulCount,
    coolCount: s.coolCount,
    sweetPhotoCount: s.sweetPhotoCount,
    commentCount: s.commentCount,
    likedByCurrentUser: s.likedByCurrentUser,
    beautifulByCurrentUser: s.beautifulByCurrentUser,
    coolByCurrentUser: s.coolByCurrentUser,
    sweetPhotoByCurrentUser: s.sweetPhotoByCurrentUser,
    isSummary: s.isSummary,
    createdAt: s.createdAt,
    updatedAt: s.updatedAt,
    viewCount: s.viewCount ?? 0,
  };
}

function garageModApiToMod(m: GarageModApi): GarageMod {
  return {
    id: m.id,
    garageCarId: m.garageCarId,
    category: m.category as GarageMod['category'],
    title: m.title,
    details: m.details,
    dateCompleted: m.dateCompleted,
    images: Array.isArray(m.images) ? m.images.map((img) => ({ url: img.url, caption: img.caption })) : [],
    createdAt: m.createdAt,
    updatedAt: m.updatedAt,
  };
}

function trackSessionApiToSession(s: import('../api/client').TrackSessionApi): TrackSession {
  return {
    id: s.id,
    garageCarId: s.garageCarId,
    venueName: s.venueName,
    sessionDate: s.sessionDate,
    eventName: s.eventName,
    eventType: s.eventType,
    bestLapTimeSeconds: s.bestLapTimeSeconds,
    bestLapTimeDisplay: s.bestLapTimeDisplay,
    slowestLapTimeSeconds: s.slowestLapTimeSeconds,
    slowestLapTimeDisplay: s.slowestLapTimeDisplay,
    lapCount: s.lapCount,
    tiresUsed: s.tiresUsed,
    notes: s.notes,
    createdAt: s.createdAt,
    updatedAt: s.updatedAt,
  };
}

function maintenanceRecordApiToRecord(r: MaintenanceRecordApi): MaintenanceRecord {
  return {
    id: r.id,
    garageCarId: r.garageCarId,
    serviceType: r.serviceType,
    title: r.title,
    performedOn: r.performedOn,
    performedOdometer: r.performedOdometer,
    notes: r.notes,
    cost: r.cost,
    shopName: r.shopName,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

function maintenanceScheduleApiToItem(item: MaintenanceScheduleItemApi): MaintenanceScheduleItem {
  return {
    serviceType: item.serviceType,
    title: item.title,
    description: item.description,
    intervalMiles: item.intervalMiles,
    intervalMonths: item.intervalMonths,
    dueDate: item.dueDate,
    predictedDueDate: item.predictedDueDate,
    dueOdometer: item.dueOdometer,
    remainingMiles: item.remainingMiles,
    status: item.status,
    dueReason: item.dueReason,
    lastCompletedOn: item.lastCompletedOn,
    lastCompletedOdometer: item.lastCompletedOdometer,
  };
}

function garageApiToCar(g: GarageCarApi): GarageCar {
  const images = Array.isArray(g.images) ? g.images : [];
  const photos = Array.isArray(g.photos) ? g.photos : images.map((img) => img.url);
  const garageMods = Array.isArray(g.garageMods)
    ? g.garageMods.map(garageModApiToMod)
    : undefined;
  const trackSessions = Array.isArray(g.trackSessions)
    ? g.trackSessions.map(trackSessionApiToSession)
    : undefined;
  return {
    id: g.id,
    ownerId: g.ownerId,
    name: g.name,
    car: g.car as unknown as GarageCar['car'],
    year: g.year,
    trim: g.trim,
    engine: g.engine,
    mods: Array.isArray(g.mods) ? g.mods : undefined,
    garageMods,
    trackSessions,
    performanceSpecs: g.performanceSpecs && typeof g.performanceSpecs === 'object' && !Array.isArray(g.performanceSpecs)
      ? g.performanceSpecs
      : undefined,
    colorCode: g.colorCode,
    colorName: g.colorName,
    vin: g.vin,
    currentOdometer: g.currentOdometer,
    averageMilesPerMonth: g.averageMilesPerMonth,
    maintenanceRemindersEnabled: g.maintenanceRemindersEnabled,
    lastOdometerUpdatedAt: g.lastOdometerUpdatedAt,
    images: images.map((img) => ({ url: img.url, caption: img.caption })),
    photos,
    buildProgressPhotos: Array.isArray(g.buildProgressPhotos) ? g.buildProgressPhotos : undefined,
    isPastCar: g.isPastCar,
    acquiredDate: g.acquiredDate,
    soldDate: g.soldDate,
    forSale: g.isForSale,
    saleSellingDetails: g.saleSellingDetails,
    saleCarCondition: g.saleCarCondition,
    saleCarfaxLink: g.saleCarfaxLink,
    saleCarFeatures: g.saleCarFeatures,
    salePrice: g.salePrice,
    saleMilesDriven: g.saleMilesDriven,
    isPrimary: g.isPrimary,
    likes: Array.isArray(g.likes) ? g.likes : [],
    comments: Array.isArray(g.comments) ? g.comments.map((comment) => ({ ...comment })) : [],
    maintenanceRecords: Array.isArray(g.maintenanceRecords) ? g.maintenanceRecords.map(maintenanceRecordApiToRecord) : undefined,
    maintenanceSchedule: Array.isArray(g.maintenanceSchedule) ? g.maintenanceSchedule.map(maintenanceScheduleApiToItem) : undefined,
    createdAt: g.createdAt,
    updatedAt: g.updatedAt,
  };
}

interface DataContextType {
  spots: Spot[];
  spotStats: SpotFeedStatsApi | null;
  garageCars: GarageCar[];
  favoriteLists: FavoriteList[];
  getAuthor: (id: string) => User | undefined;
  addSpot: (spot: Omit<Spot, 'id' | 'createdAt' | 'likes' | 'comments' | 'beautiful' | 'cool' | 'sweetPhoto' | 'imageUrl' | 'images' | 'viewCount'> & { images: Array<{ filename: string; imageHash?: string }> }) => Promise<Spot>;
  updateSpot: (id: string, patch: Partial<Spot>) => Promise<Spot>;
  removeSpot: (id: string) => Promise<void>;
  likeSpot: (spotId: string, _userId: string) => Promise<void>;
  unlikeSpot: (spotId: string, _userId: string) => Promise<void>;
  beautifulSpot: (spotId: string, _userId: string) => Promise<void>;
  unBeautifulSpot: (spotId: string, _userId: string) => Promise<void>;
  coolSpot: (spotId: string, _userId: string) => Promise<void>;
  unCoolSpot: (spotId: string, _userId: string) => Promise<void>;
  sweetPhotoSpot: (spotId: string, _userId: string) => Promise<void>;
  unSweetPhotoSpot: (spotId: string, _userId: string) => Promise<void>;
  addComment: (spotId: string, comment: Pick<Comment, 'authorId' | 'body' | 'replyToCommentId'>) => Promise<void>;
  addGarageCar: (car: Omit<GarageCar, 'id' | 'createdAt' | 'updatedAt' | 'likes' | 'comments'>) => Promise<GarageCar>;
  updateGarageCar: (id: string, patch: Partial<GarageCar>) => Promise<void>;
  removeGarageCar: (id: string) => Promise<void>;
  addToFavorites: (listId: string, spotId: string) => Promise<void>;
  removeFromFavorites: (listId: string, spotId: string) => Promise<void>;
  createFavoriteList: (name: string, userId: string) => Promise<FavoriteList>;
  getSpotsInList: (listId: string) => Spot[];
  loading: boolean;
  loadingMoreSpots: boolean;
  hasMoreSpots: boolean;
  error: string | null;
  refreshSpots: () => Promise<void>;
  loadMoreSpots: () => Promise<void>;
  refreshGarage: () => Promise<void>;
  refreshFavorites: () => Promise<void>;
  fetchSpot: (spotId: string, options?: { incrementView?: boolean }) => Promise<Spot | undefined>;
  recordSpotView: (spotId: string) => Promise<void>;
}

const DataContext = createContext<DataContextType | null>(null);

export function DataProvider({ children }: { children: ReactNode }) {
  const { getAuthor, isAuthenticated } = useAuth();
  const [spots, setSpots] = useState<Spot[]>([]);
  const [spotStats, setSpotStats] = useState<SpotFeedStatsApi | null>(null);
  const [garageCars, setGarageCars] = useState<GarageCar[]>([]);
  const [favoriteLists, setFavoriteLists] = useState<FavoriteList[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMoreSpots, setLoadingMoreSpots] = useState(false);
  const [hasMoreSpots, setHasMoreSpots] = useState(false);
  const [nextSpotsCursor, setNextSpotsCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshSpots = useCallback(async () => {
    setLoading(true);
    try {
      const page = await spotsApi.feed({ limit: INITIAL_SPOT_PAGE_SIZE, includeStats: true });
      setSpots(page.items.map(spotApiToSpot));
      setSpotStats(page.stats ?? null);
      setNextSpotsCursor(page.nextCursor);
      setHasMoreSpots(page.hasMore);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load spots');
      setSpots([]);
      setSpotStats(null);
      setNextSpotsCursor(null);
      setHasMoreSpots(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMoreSpots = useCallback(async () => {
    if (loading || loadingMoreSpots || !hasMoreSpots || !nextSpotsCursor) return;
    setLoadingMoreSpots(true);
    try {
      const page = await spotsApi.feed({ limit: INITIAL_SPOT_PAGE_SIZE, cursor: nextSpotsCursor });
      setSpots((prev) => mergeSpotsById(prev, page.items.map(spotApiToSpot)));
      setNextSpotsCursor(page.nextCursor);
      setHasMoreSpots(page.hasMore);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load more spots');
    } finally {
      setLoadingMoreSpots(false);
    }
  }, [hasMoreSpots, loading, loadingMoreSpots, nextSpotsCursor]);

  const refreshGarage = useCallback(async () => {
    if (!isAuthenticated) {
      setGarageCars([]);
      return;
    }
    try {
      const list = await garageApi.list();
      setGarageCars(list.map(garageApiToCar));
      setError(null);
    } catch (e) {
      setGarageCars([]);
    }
  }, [isAuthenticated]);

  const refreshFavorites = useCallback(async () => {
    if (!isAuthenticated) {
      setFavoriteLists([]);
      return;
    }
    try {
      const list = await favoritesApi.list();
      setFavoriteLists(list.map((f) => ({ ...f, spotIds: f.spotIds })));
    } catch {
      setFavoriteLists([]);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void refreshSpots();
  }, [refreshSpots]);

  useEffect(() => {
    void refreshGarage();
    void refreshFavorites();
  }, [refreshGarage, refreshFavorites]);

  const addSpot = useCallback(
    async (input: Omit<Spot, 'id' | 'createdAt' | 'likes' | 'comments' | 'beautiful' | 'cool' | 'sweetPhoto' | 'imageUrl' | 'images' | 'viewCount'> & { images: Array<{ filename: string; imageHash?: string }> }) => {
      const s = await spotsApi.create({
        images: input.images,
        caption: input.caption,
        car: input.car as unknown as Record<string, string | undefined>,
        location: input.location,
        city: input.city,
        countryCode: input.countryCode,
        event: input.event,
        taggedOwnerId: input.taggedOwnerId,
      });
      const spot = spotApiToSpot(s);
      setSpots((prev) => mergeSpotsById([spot], prev));
      setSpotStats((prev) => prev ? { ...prev, totalCount: prev.totalCount + 1 } : prev);
      return spot;
    },
    []
  );

  const updateSpot = useCallback(async (id: string, patch: Partial<Spot>): Promise<Spot> => {
    const body: Record<string, unknown> = {};
    if (patch.caption !== undefined) body.caption = patch.caption;
    if (patch.location !== undefined) body.location = patch.location;
    if (patch.city !== undefined) body.city = patch.city;
    if (patch.countryCode !== undefined) body.countryCode = patch.countryCode;
    if (patch.event !== undefined) body.event = patch.event;
    if (patch.taggedOwnerId !== undefined) body.taggedOwnerId = patch.taggedOwnerId;
    if (patch.car !== undefined) body.car = patch.car;
    if (patch.images !== undefined && Array.isArray(patch.images)) {
      body.images = patch.images.map((url) => {
        if (typeof url !== 'string') return '';
        const m = url.match(/\/uploads\/(.+)$/);
        return m ? m[1] : url;
      }).filter(Boolean);
    }
    const s = await spotsApi.update(id, body);
    const updated = spotApiToSpot(s);
    setSpots((prev) => prev.map((p) => (p.id === id ? updated : p)));
    return updated;
  }, []);

  const removeSpot = useCallback(async (id: string): Promise<void> => {
    await spotsApi.delete(id);
    setSpots((prev) => prev.filter((spot) => spot.id !== id));
    setSpotStats((prev) => prev ? { ...prev, totalCount: Math.max(0, prev.totalCount - 1) } : prev);
    setFavoriteLists((prev) =>
      prev.map((list) => ({
        ...list,
        spotIds: list.spotIds.filter((spotId) => spotId !== id),
      }))
    );
  }, []);

  const likeSpot = useCallback(async (spotId: string) => {
    const s = await spotsApi.like(spotId);
    const spot = spotApiToSpot(s);
    setSpots((prev) => prev.map((p) => (p.id === spotId ? spot : p)));
  }, []);

  const unlikeSpot = useCallback(async (spotId: string) => {
    const s = await spotsApi.unlike(spotId);
    const spot = spotApiToSpot(s);
    setSpots((prev) => prev.map((p) => (p.id === spotId ? spot : p)));
  }, []);

  const beautifulSpot = useCallback(async (spotId: string) => {
    const s = await spotsApi.beautiful(spotId);
    const spot = spotApiToSpot(s);
    setSpots((prev) => prev.map((p) => (p.id === spotId ? spot : p)));
  }, []);

  const unBeautifulSpot = useCallback(async (spotId: string) => {
    const s = await spotsApi.unBeautiful(spotId);
    const spot = spotApiToSpot(s);
    setSpots((prev) => prev.map((p) => (p.id === spotId ? spot : p)));
  }, []);

  const coolSpot = useCallback(async (spotId: string) => {
    const s = await spotsApi.cool(spotId);
    const spot = spotApiToSpot(s);
    setSpots((prev) => prev.map((p) => (p.id === spotId ? spot : p)));
  }, []);

  const unCoolSpot = useCallback(async (spotId: string) => {
    const s = await spotsApi.unCool(spotId);
    const spot = spotApiToSpot(s);
    setSpots((prev) => prev.map((p) => (p.id === spotId ? spot : p)));
  }, []);

  const sweetPhotoSpot = useCallback(async (spotId: string) => {
    const s = await spotsApi.sweetPhoto(spotId);
    const spot = spotApiToSpot(s);
    setSpots((prev) => prev.map((p) => (p.id === spotId ? spot : p)));
  }, []);

  const unSweetPhotoSpot = useCallback(async (spotId: string) => {
    const s = await spotsApi.unSweetPhoto(spotId);
    const spot = spotApiToSpot(s);
    setSpots((prev) => prev.map((p) => (p.id === spotId ? spot : p)));
  }, []);

  const addComment = useCallback(async (spotId: string, comment: Pick<Comment, 'authorId' | 'body' | 'replyToCommentId'>) => {
    const s = await spotsApi.addComment(spotId, comment.body, comment.replyToCommentId);
    const spot = spotApiToSpot(s);
    setSpots((prev) => prev.map((p) => (p.id === spotId ? spot : p)));
  }, []);

  const addGarageCar = useCallback(async (input: Omit<GarageCar, 'id' | 'createdAt' | 'updatedAt' | 'likes' | 'comments'>) => {
    const images =
      input.images?.length > 0
        ? input.images.map((img) => ({ imageUrl: img.url, caption: img.caption ?? null }))
        : (input.photos ?? []).map((url) => ({ imageUrl: url, caption: null }));
    const body = {
      name: input.name,
      car: input.car,
      year: input.year,
      trim: input.trim,
      engine: input.engine,
      mods: input.mods,
      performanceSpecs: input.performanceSpecs,
      colorCode: input.colorCode,
      colorName: input.colorName,
      vin: input.vin,
      currentOdometer: input.currentOdometer,
      averageMilesPerMonth: input.averageMilesPerMonth,
      maintenanceRemindersEnabled: input.maintenanceRemindersEnabled,
      images,
      buildProgressPhotos: input.buildProgressPhotos,
      isPastCar: input.isPastCar,
      acquiredDate: input.acquiredDate,
      soldDate: input.soldDate,
      isForSale: input.forSale,
      isPrimary: input.isPrimary,
      saleSellingDetails: input.saleSellingDetails,
      saleCarCondition: input.saleCarCondition,
    saleCarfaxLink: input.saleCarfaxLink,
    saleCarFeatures: input.saleCarFeatures,
    salePrice: input.salePrice,
    saleMilesDriven: input.saleMilesDriven,
  };
    const g = await garageApi.create(body);
    const car = garageApiToCar(g);
    setGarageCars((prev) => [...prev, car]);
    return car;
  }, []);

  const updateGarageCar = useCallback(async (id: string, patch: Partial<GarageCar>) => {
    const body: Record<string, unknown> = {
      name: patch.name,
      car: patch.car ?? {},
      year: patch.year,
      trim: patch.trim,
      engine: patch.engine,
      mods: patch.mods,
      performanceSpecs: patch.performanceSpecs ?? {},
      colorCode: patch.colorCode,
      colorName: patch.colorName,
      vin: patch.vin,
      currentOdometer: patch.currentOdometer,
      averageMilesPerMonth: patch.averageMilesPerMonth,
      maintenanceRemindersEnabled: patch.maintenanceRemindersEnabled,
      isPastCar: patch.isPastCar,
      acquiredDate: patch.acquiredDate,
      soldDate: patch.soldDate,
      isForSale: patch.forSale,
      isPrimary: patch.isPrimary,
      // Always send sale fields when present so they are not omitted by JSON.stringify(undefined)
      saleSellingDetails: patch.saleSellingDetails ?? '',
      saleCarCondition: patch.saleCarCondition ?? '',
      saleCarfaxLink: patch.saleCarfaxLink ?? '',
      saleCarFeatures: patch.saleCarFeatures ?? '',
      salePrice: patch.salePrice ?? '',
      saleMilesDriven: patch.saleMilesDriven,
    };
    if (patch.images !== undefined) {
      body.images = patch.images.map((img) => ({ imageUrl: img.url, caption: img.caption ?? null }));
    }
    const g = await garageApi.update(id, body);
    setGarageCars((prev) => prev.map((c) => (c.id === id ? garageApiToCar(g) : c)));
  }, []);

  const removeGarageCar = useCallback(async (id: string) => {
    await garageApi.delete(id);
    setGarageCars((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const addToFavorites = useCallback(async (listId: string, spotId: string) => {
    await favoritesApi.addSpot(listId, spotId);
    setFavoriteLists((prev) =>
      prev.map((f) => (f.id === listId ? { ...f, spotIds: [...f.spotIds, spotId] } : f))
    );
  }, []);

  const removeFromFavorites = useCallback(async (listId: string, spotId: string) => {
    await favoritesApi.removeSpot(listId, spotId);
    setFavoriteLists((prev) =>
      prev.map((f) => (f.id === listId ? { ...f, spotIds: f.spotIds.filter((id) => id !== spotId) } : f))
    );
  }, []);

  const createFavoriteList = useCallback(async (name: string, userId: string) => {
    const list = await favoritesApi.create(name);
    const fav: FavoriteList = { ...list, userId };
    setFavoriteLists((prev) => [...prev, fav]);
    return fav;
  }, []);

  const getSpotsInList = useCallback(
    (listId: string) => {
      const list = favoriteLists.find((f) => f.id === listId);
      if (!list) return [];
      return list.spotIds.map((id) => spots.find((s) => s.id === id)).filter(Boolean) as Spot[];
    },
    [favoriteLists, spots]
  );

  const fetchSpot = useCallback(async (spotId: string, options?: { incrementView?: boolean }) => {
    try {
      const s = await spotsApi.get(spotId, options);
      const spot = spotApiToSpot(s);
      setSpots((prev) => mergeSpotsById(prev, [spot]));
      return spot;
    } catch {
      return undefined;
    }
  }, []);

  const recordSpotView = useCallback(async (spotId: string) => {
    await fetchSpot(spotId, { incrementView: true });
  }, [fetchSpot]);

  const value: DataContextType = {
    spots,
    spotStats,
    garageCars,
    favoriteLists,
    getAuthor,
    addSpot,
    updateSpot,
    removeSpot,
    likeSpot,
    unlikeSpot,
    beautifulSpot,
    unBeautifulSpot,
    coolSpot,
    unCoolSpot,
    sweetPhotoSpot,
    unSweetPhotoSpot,
    addComment,
    addGarageCar,
    updateGarageCar,
    removeGarageCar,
    addToFavorites,
    removeFromFavorites,
    createFavoriteList,
    getSpotsInList,
    loading,
    loadingMoreSpots,
    hasMoreSpots,
    error,
    refreshSpots,
    loadMoreSpots,
    refreshGarage,
    refreshFavorites,
    fetchSpot,
    recordSpotView,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within DataProvider');
  return ctx;
}
